import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../service/admin/adminApiService';

export default function PriceCalc({
  open,
  onClose,
  variation,
  onApplyPrice, // (finalPriceNumber, wholesaleFinalPrice) => void
  onApplyDimensions, // (dimensions) => void - { weight, length, height, width }
  localShipping,
  defaultCategoryId, // optional
  pricingMode = 'dropshipping', // "dropshipping" or "wholesale"
  allVariants = null, // Array of all variants for "Apply to All" functionality
  onApplyToAllVariants = null, // (dimensions, pricesByVariant) => void - { weight, length, height, width }, { variantKey: { finalPrice, wholesaleFinalPrice } }
  onApplyToAllStart = null, // () => void - Called when processing starts
  onApplyToAllProgress = null, // (progress) => void - Called with progress 0-100
  onApplyToAllComplete = null, // (results) => void - Called with results array
  onShowSingleVariantModal = null, // New callback for single variant modal
}) {
  // --- STATE ---
  const [calculator, setCalculator] = useState({
    ap: '', // AP in RMB
    cp: '', // CP in RMB
    ls: localShipping || '', // Local shipping RMB (Product ls)
    hsn: false,
    hsnTaxPercent: 12,
    dw: '', // Dead weight in kg
    weight: '', // Weight in kg
    length: '', // Length in cm
    height: '', // Height in cm
    width: '', // Width in cm
    // categoryId: defaultCategoryId || "", // Category logic currently not in use
    formula: {
      rmb_to_inr_rate: 15,
      markup_percent: 0.15,
      customs_duty_default: 0.22,
      customs_duty_surcharge: 0.1,
      international_freight_per_kg: 700,
      weight_buffer_percent: 0.05,
      ddefault_chargeable_weight: 0.5,
      last_mile_base: 100,
      last_mile_extra_per_500g: 40,
      platform_fee_percent: 0.075,
      gst_default: 0.18,
      gst_freight: 0.18,
      gst_platform_fee: 0.18,
    },
  });

  const [result, setResult] = useState(null);
  const [wsResult, setWsResult] = useState(null); // WS Final Price result (CP-based)
  const [formulaLoading, setFormulaLoading] = useState(false);
  const [formulaError, setFormulaError] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  const [applicableAlgorithmName, setApplicableAlgorithmName] = useState('');

  // --- AUTOFILL BASIC VALUES FROM VARIANT ---
  useEffect(() => {
    if (!variation) return;

    // Get AP (Actual Price) from variant
    const apValue = variation.ap || variation.fenxiaoPriceInfo?.onePiecePrice || variation.price?.rmb || variation.rmb || '';

    // Get CP (Consign Price) from variant
    const cpValue = variation.cp || variation.consignPrice || variation.fenxiaoPriceInfo?.onePiecePrice || '';

    setCalculator(prev => ({
      ...prev,

      // AP in RMB
      ap: apValue || prev.ap,
      // CP in RMB
      cp: cpValue || prev.cp,

      // Local shipping RMB
      ls: localShipping || prev.ls,

      // Dead weight (assuming already in kg or as required)
      dw: Number(variation?.shippingDetail?.weight ?? variation?.weight ?? prev.dw ?? 0),

      // Dimensions
      weight: variation?.weight ?? variation?.shippingDetail?.weight ?? prev.weight ?? '',
      length: variation?.length ?? variation?.shippingDetail?.length ?? prev.length ?? '',
      height: variation?.height ?? variation?.shippingDetail?.height ?? prev.height ?? '',
      width: variation?.width ?? variation?.shippingDetail?.width ?? prev.width ?? '',

      // categoryId: defaultCategoryId || prev.categoryId,
    }));

    // Clear previous calculation result when variant changes
    setResult(null);
    setWsResult(null);
    setCalculationError('');
    setApplicableAlgorithmName('');
  }, [variation, defaultCategoryId, localShipping, pricingMode]);

  // --- EARLY RETURN AFTER HOOKS ---
  if (!open || !variation) return null;

  // --- HANDLERS ---

  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    // Handle nested formula.* inputs
    if (name.startsWith('formula.')) {
      const formulaKey = name.split('.')[1];
      setCalculator(prev => ({
        ...prev,
        formula: {
          ...prev.formula,
          [formulaKey]: type === 'number' || type === 'range' ? Number(value) : value,
        },
      }));
    } else {
      // Handle top-level fields (ap, ls, dw, hsn, hsnTaxPercent, etc.)
      setCalculator(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const applyFormulaByAlgorithmName = async algorithmName => {
    if (!algorithmName) return;

    try {
      const algoRes = await adminApiService.get('/pricing-algorithms', {
        params: { _t: Date.now() },
      });

      const algorithms = algoRes?.data || [];
      const applicableAlgo = algorithms.find(algo => algo && algo.name === algorithmName && algo.active && !algo.deleted);

      if (applicableAlgo?.formula) {
        setCalculator(prev => ({
          ...prev,
          formula: {
            ...prev.formula,
            ...applicableAlgo.formula,
          },
        }));
      }
    } catch (e) {
      console.error('Failed to apply formula by algorithm name', e);
    }
  };

  const calculateViaAPI = async (useCP = false) => {
    const { ap, cp, ls, hsn, hsnTaxPercent, dw, weight } = calculator;

    const DEFAULT_CW = Number(calculator?.formula?.default_chargeable_weight || 0.5); // 0.5kg

    // Use weight from calculator state if available, otherwise use dw
    const deadWeightKg = Number(weight || dw || 0);
    const hasDeadWeight = deadWeightKg > 0;

    // Use dimensions from calculator state if available, otherwise fallback to variation
    const L = Number(calculator.length || (variation.shippingDetail?.length ?? variation.length ?? 0));
    const B = Number(calculator.width || (variation.shippingDetail?.width ?? variation.width ?? 0));
    const H = Number(calculator.height || (variation.shippingDetail?.height ?? variation.height ?? 0));

    const hasDims = L > 0 && B > 0 && H > 0;
    let volumetricWeightKg = hasDims ? (L * B * H) / 5000 : DEFAULT_CW;

    // Determine chargeable weight source for display
    let chargeableWeightSource;
    if (!hasDims && !hasDeadWeight) {
      chargeableWeightSource = 'default'; // both missing => DEFAULT_CW
    } else if (!hasDims && hasDeadWeight) {
      // dimensions missing => compare deadWeight vs DEFAULT_CW
      chargeableWeightSource = deadWeightKg >= DEFAULT_CW ? 'dead' : 'default';
    } else if (hasDims && !hasDeadWeight) {
      // weight missing => compare volumetric vs DEFAULT_CW
      chargeableWeightSource = volumetricWeightKg >= DEFAULT_CW ? 'volumetric' : 'default';
    } else {
      // both present => max(volumetric, dead)
      chargeableWeightSource = volumetricWeightKg >= deadWeightKg ? 'volumetric' : 'dead';
    }

    // IMPORTANT: ls must be in RMB (your UI already says RMB)
    const lsInRMB = Number(ls || 0);

    // when weight is missing, send default DW to API (so API also follows same rule)
    const dwForApi = hasDeadWeight ? deadWeightKg : DEFAULT_CW;

    // Use input field values: CP for WS calculation, AP for DS calculation
    const priceValue = useCP ? Number(cp || 0) : Number(ap || 0);

    const payload = {
      ap: priceValue, // Use CP if useCP is true, otherwise use AP
      ls: lsInRMB, // RMB
      hsn: hsn || false,
      dw: dwForApi,
      vw: volumetricWeightKg, // if dims missing => DEFAULT_CW
      hsnTaxPercent: hsnTaxPercent || 12,
      categoryId: defaultCategoryId || '',
      sku: variation.sku || variation.skuId || null,
    };

    try {
      const response = await adminApiService.post('/pricing-algorithms/calculate', payload);

      const breakdown = response?.data?.breakdown || response?.breakdown;
      const algoName = response?.data?.algorithm || response?.algorithm || '';

      if (algoName && !useCP) {
        // Only set algorithm name for AP calculation
        setApplicableAlgorithmName(algoName);
        await applyFormulaByAlgorithmName(algoName);
      }

      if (!breakdown) {
        throw new Error('Invalid response from API: breakdown not found');
      }

      return {
        breakdown: {
          AP_RMB: breakdown.AP_RMB || 0,
          AP_INR: Math.round(breakdown.AP_INR || 0),
          MP: Math.round(breakdown.MP || 0),
          CP: Math.round(breakdown.CP || 0),
          LS: Math.round(breakdown.LS || 0),
          CD: Math.round(breakdown.CD || 0),
          IF: Math.round(breakdown.IF || 0),
          LMD: Math.round(breakdown.LMD || 0),
          LC: Math.round(breakdown.LC || 0),
          PF: Math.round(breakdown.PF || 0),
          GST_PR: Math.round(breakdown.GST_PR || 0),
          GST_FR: Math.round(breakdown.GST_FR || 0),
          GST_PF: Math.round(breakdown.GST_PF || 0),
          FBP: Math.round(breakdown.FBP || 0),

          volumetricWeightKg: Math.round(volumetricWeightKg * 1000) / 1000,
          deadWeightKg: Math.round(dwForApi * 1000) / 1000,
          chargeableWeight: breakdown.chargeableWeight ? Math.round(breakdown.chargeableWeight * 1000) / 1000 : Math.round(Math.max(volumetricWeightKg, dwForApi) * 1000) / 1000,
          chargeableWeightSource,
        },
      };
    } catch (error) {
      console.error(`Failed to calculate ${useCP ? 'WS' : ''} price via API:`, error);
      throw error;
    }
  };
  const handleCalculate = async () => {
    try {
      setCalculating(true);
      setCalculationError('');
      setResult(null);
      setWsResult(null);

      // Calculate both AP-based (finalPrice) and CP-based (wholesaleFinalPrice) prices
      const [apResult, wsResultData] = await Promise.all([
        calculateViaAPI(false), // AP-based calculation
        calculateViaAPI(true).catch(err => {
          console.error('WS calculation failed:', err);
          return null; // Don't fail entire calculation if WS fails
        }),
      ]);

      setResult(apResult);
      setWsResult(wsResultData);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Failed to calculate price. Please try again.';
      setCalculationError(msg);
      setResult(null);
      setWsResult(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleApply = () => {
    if (!result) return;
    const finalPrice = result.breakdown.FBP;
    const wholesaleFinalPrice = wsResult?.breakdown?.FBP || null;

    const dimensions = {
      weight: calculator.weight ? Number(calculator.weight) : null,
      length: calculator.length ? Number(calculator.length) : null,
      height: calculator.height ? Number(calculator.height) : null,
      width: calculator.width ? Number(calculator.width) : null,
    };

    // Create single variant result in the same format as apply to all
    const singleVariantResult = {
      variantKey: variation.skuId || variation.sku,
      variantSkuId: variation.skuId || variation.sku,
      finalPrice,
      wholesaleFinalPrice,
      variant: {
        ...variation,
        imgUrl: variation.imgUrl || [],
        image: variation.image || '',
        weight: variation.weight ?? null,
        length: variation.length ?? null,
        width: variation.width ?? null,
        height: variation.height ?? null,
      },
    };

    // Show modal using the same callback as apply to all
    if (onShowSingleVariantModal) {
      onShowSingleVariantModal([singleVariantResult], dimensions);
    } else {
      // Fallback to original behavior if callback not provided
      if (onApplyDimensions) {
        onApplyDimensions(dimensions);
      }
      onApplyPrice(finalPrice, wholesaleFinalPrice);
      onClose();
    }
  };

  const handleApplyToAll = async () => {
    if (!result || !allVariants || !onApplyToAllVariants) return;

    const dimensions = {
      weight: calculator.weight ? Number(calculator.weight) : null,
      length: calculator.length ? Number(calculator.length) : null,
      height: calculator.height ? Number(calculator.height) : null,
      width: calculator.width ? Number(calculator.width) : null,
    };

    // Calculate prices for all variants using the same dimensions
    const pricesByVariant = {};
    const DEFAULT_CW = 0.5;
    const lsInRMB = Number(calculator.ls || 0);

    // Calculate dw and vw from dimensions
    const deadWeightKg = Number(calculator.weight || calculator.dw || 0);
    const hasDeadWeight = deadWeightKg > 0;
    const L = Number(calculator.length || 0);
    const B = Number(calculator.width || 0);
    const H = Number(calculator.height || 0);
    const hasDims = L > 0 && B > 0 && H > 0;
    const volumetricWeightKg = hasDims ? (L * B * H) / 5000 : DEFAULT_CW;
    const dwForApi = hasDeadWeight ? deadWeightKg : DEFAULT_CW;

    try {
      setCalculating(true);
      setCalculationError('');

      // Notify parent that processing has started
      if (onApplyToAllStart) {
        onApplyToAllStart();
      }

      // Calculate prices for all variants with progress tracking
      const totalVariants = allVariants.length;
      let completedCount = 0;

      const pricePromises = allVariants.map(async (variant, index) => {
        const variantKey = variant?.id || variant?.skuId;
        if (!variantKey) return null;

        // Get AP and CP for this variant - MUST use variant's own values
        // Each variant should have its own AP and CP for accurate pricing
        // Try multiple possible locations for AP
        const ap = variant?.ap || variant?.price?.rmb || variant?.price?.ap || variant?.fenxiaoPriceInfo?.onePiecePrice || 0;

        // Try multiple possible locations for CP - each variant has its own CP
        const cp = variant?.cp || variant?.consignPrice || variant?.price?.cp || variant?.fenxiaoPriceInfo?.onePiecePrice || 0;

        console.log(`[ApplyToAll] Variant ${variantKey} (${variant?.skuId || variant?.sku}): AP=${ap}, CP=${cp}`);

        // Calculate AP-based price (finalPrice) and CP-based price (wholesaleFinalPrice) in parallel
        let finalPrice = null;
        let wholesaleFinalPrice = null;

        // Create promises for both calculations
        const calculations = [];

        // AP-based calculation (finalPrice)
        if (ap > 0) {
          calculations.push(
            adminApiService
              .post('/pricing-algorithms/calculate', {
                ap: Number(ap),
                ls: lsInRMB,
                hsn: false,
                dw: dwForApi,
                vw: volumetricWeightKg,
                hsnTaxPercent: 12,
                categoryId: defaultCategoryId || '',
                sku: variant?.sku || variant?.skuId || null,
              })
              .then(response => {
                const breakdown = response?.data?.breakdown || response?.breakdown;
                if (breakdown && breakdown.FBP) {
                  return { type: 'ap', price: Math.round(breakdown.FBP || 0) };
                }
                return null;
              })
              .catch(error => {
                console.error(`Failed to calculate finalPrice for variant ${variantKey}:`, error);
                return null;
              })
          );
        }

        // CP-based calculation (wholesaleFinalPrice) - ALWAYS calculate if CP > 0
        if (cp > 0) {
          calculations.push(
            adminApiService
              .post('/pricing-algorithms/calculate', {
                ap: Number(cp), // Use CP as AP in payload
                ls: lsInRMB,
                hsn: false,
                dw: dwForApi,
                vw: volumetricWeightKg,
                hsnTaxPercent: 12,
                categoryId: defaultCategoryId || '',
                sku: variant?.sku || variant?.skuId || null,
              })
              .then(response => {
                const breakdown = response?.data?.breakdown || response?.breakdown;
                if (breakdown && breakdown.FBP) {
                  return { type: 'cp', price: Math.round(breakdown.FBP || 0) };
                }
                return null;
              })
              .catch(error => {
                console.error(`Failed to calculate wholesaleFinalPrice for variant ${variantKey}:`, error);
                return null;
              })
          );
        }

        // Wait for both calculations to complete
        const results = await Promise.all(calculations);
        results.forEach(result => {
          if (result) {
            if (result.type === 'ap') {
              finalPrice = result.price;
            } else if (result.type === 'cp') {
              wholesaleFinalPrice = result.price;
            }
          }
        });

        console.log(`[ApplyToAll] Variant ${variantKey}: AP=${ap}, CP=${cp}, finalPrice=${finalPrice}, wholesaleFinalPrice=${wholesaleFinalPrice}`);

        // Update progress
        completedCount++;
        const progress = Math.round((completedCount / totalVariants) * 100);
        if (onApplyToAllProgress) {
          onApplyToAllProgress(progress);
        }

        return {
          variantKey,
          variantSkuId: variant?.skuId || variant?.sku || variantKey,
          finalPrice,
          wholesaleFinalPrice,
        };
      });

      const calculatedResults = await Promise.all(pricePromises);

      // Process results
      const finalResults = [];
      calculatedResults.forEach(result => {
        if (result) {
          pricesByVariant[result.variantKey] = {
            finalPrice: result.finalPrice,
            wholesaleFinalPrice: result.wholesaleFinalPrice,
          };

          // Find original variant data to include complete information
          const originalVariant = allVariants?.find(v => (v.skuId || v.sku) === result.variantKey || (v.skuId || v.sku) === result.variantSkuId);
          finalResults.push({
            variantKey: result.variantKey,
            variantSkuId: result.variantSkuId,
            finalPrice: result.finalPrice,
            wholesaleFinalPrice: result.wholesaleFinalPrice,
            variant: originalVariant
              ? {
                  ...originalVariant,
                  imgUrl: originalVariant.imgUrl || [],
                  image: originalVariant.image || '',
                  weight: originalVariant.weight ?? null,
                  length: originalVariant.length ?? null,
                  width: originalVariant.width ?? null,
                  height: originalVariant.height ?? null,
                }
              : {
                  skuId: result.variantSkuId,
                  sku: result.variantSkuId,
                  imgUrl: [],
                  image: '',
                  weight: null,
                  length: null,
                  width: null,
                  height: null,
                  skuAttributes: [],
                },
          });
        }
      });

      // Notify parent that processing is complete with results (include dimensions)
      if (onApplyToAllComplete) {
        onApplyToAllComplete(finalResults, dimensions);
      }

      // Call callback with dimensions and prices for all variants
      onApplyToAllVariants(dimensions, pricesByVariant);

      onClose();
    } catch (error) {
      console.error('Failed to calculate prices for all variants:', error);
      setCalculationError('Failed to calculate prices for all variants. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  // --- JSX ---

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-start justify-start">
      {/* Left side drawer-style modal */}
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-4 py-2 shadow-xl relative z-[10000]">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Update Price for Variant</h3>
          <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-900">
            ✕
          </button>
        </div>

        {/* Formula load status */}
        {formulaLoading && <p className="text-xs text-gray-500 mb-2">Loading pricing formula from server...</p>}
        {formulaError && <p className="text-xs text-red-600 mb-2">{formulaError}</p>}
        {applicableAlgorithmName && (
          <p className="text-xs text-green-600 mb-2">
            ✓ Using algorithm: <strong>{applicableAlgorithmName}</strong>
          </p>
        )}

        {/* Basic variant info – SAME ROW */}
        <div className="mb-2 text-sm text-gray-600">
          <div className="flex items-center justify-between gap-4">
            <div>
              <strong>Variant skuId: </strong>
              {variation.sku || variation.skuId}
            </div>
            {(() => {
              // Show price based on pricing mode
              const currentPrice = pricingMode === 'wholesale' ? variation.wholesaleFinalPrice : variation.finalPrice;

              return (
                typeof currentPrice === 'number' && (
                  <div>
                    <strong>Current Final Price:</strong> ₹ {currentPrice}
                  </div>
                )
              );
            })()}
          </div>
        </div>

        {/* Product inputs */}
        <div className="mb-2">
          <h4 className="font-semibold mb-1 text-sm">Product Details (Variant)</h4>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">AP (DS)</label>
              <input type="number" name="ap" value={calculator.ap} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">CP (WS)</label>
              <input type="number" name="cp" value={calculator.cp} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">LS (RMB)</label>
              <input type="number" name="ls" value={calculator.ls} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">DW (Dead weight in kg)</label>
              <input type="number" name="dw" value={calculator.dw} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.001" />
            </div>

            {/* HSN toggles are hidden for now */}
            {/* <div className="flex items-center mt-5 space-x-2">
                            <input
                                type="checkbox"
                                name="hsn"
                                checked={calculator.hsn}
                                onChange={handleChange}
                            />
                            <span className="text-xs">
                                Has HSN specific GST / duty
                            </span>
                        </div> */}

            {calculator.hsn && (
              <div>
                <label className="block text-xs font-medium mb-1">HSN Tax %</label>
                <input
                  type="number"
                  name="hsnTaxPercent"
                  value={calculator.hsnTaxPercent}
                  onChange={handleChange}
                  className="w-full px-2 py-0.5 border rounded text-sm"
                  step="0.1"
                />
              </div>
            )}
          </div>
        </div>

        {/* Dimensions */}
        <div className="mb-2">
          <h4 className="font-semibold mb-1 text-sm">Dimensions</h4>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Weight (kg)</label>
              <input type="number" name="weight" value={calculator.weight} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.001" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Length (cm)</label>
              <input type="number" name="length" value={calculator.length} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Height (cm)</label>
              <input type="number" name="height" value={calculator.height} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Width (cm)</label>
              <input type="number" name="width" value={calculator.width} onChange={handleChange} className="w-full px-2 py-0.5 border rounded text-sm" step="0.01" />
            </div>
          </div>
        </div>

        {/* Formula parameters */}
        <div className="mb-1">
          <h4 className="font-semibold mb-1 text-sm">Formula Parameters</h4>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div>
              <label className="block mb-1">RMB → INR Rate</label>
              <input
                type="number"
                name="formula.rmb_to_inr_rate"
                value={calculator.formula.rmb_to_inr_rate}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.1"
              />
            </div>

            <div>
              <label className="block mb-1">Markup %</label>
              <input
                type="number"
                name="formula.markup_percent"
                value={calculator.formula.markup_percent}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">Customs Default %</label>
              <input
                type="number"
                name="formula.customs_duty_default"
                value={calculator.formula.customs_duty_default}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">Customs Surcharge %</label>
              <input
                type="number"
                name="formula.customs_duty_surcharge"
                value={calculator.formula.customs_duty_surcharge}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">Freight per KG</label>
              <input
                type="number"
                name="formula.international_freight_per_kg"
                value={calculator.formula.international_freight_per_kg}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
              />
            </div>

            <div>
              <label className="block mb-1">Weight Buffer %</label>
              <input
                type="number"
                name="formula.weight_buffer_percent"
                value={calculator.formula.weight_buffer_percent}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">Last-mile Base (INR)</label>
              <input
                type="number"
                name="formula.last_mile_base"
                value={calculator.formula.last_mile_base}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
              />
            </div>

            <div>
              <label className="block mb-1">Last-mile Extra /500g</label>
              <input
                type="number"
                name="formula.last_mile_extra_per_500g"
                value={calculator.formula.last_mile_extra_per_500g}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
              />
            </div>

            <div>
              <label className="block mb-1">Platform Fee %</label>
              <input
                type="number"
                name="formula.platform_fee_percent"
                value={calculator.formula.platform_fee_percent}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">GST Product %</label>
              <input
                type="number"
                name="formula.gst_default"
                value={calculator.formula.gst_default}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">GST Freight %</label>
              <input
                type="number"
                name="formula.gst_freight"
                value={calculator.formula.gst_freight}
                onChange={handleChange}
                className="w-full px-2 py-0.5 border rounded text-sm"
                step="0.01"
              />
            </div>

            <div>
              <label className="block mb-1">GST Platform Fee %</label>
              <input
                type="number"
                name="formula.gst_platform_fee"
                value={calculator.formula.gst_platform_fee}
                onChange={handleChange}
                className="w-full px-2 py-1 border rounded text-sm"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {calculationError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{calculationError}</div>}

        {/* Buttons */}
        <div className="flex gap-3 mb-2">
          <button
            type="button"
            onClick={handleCalculate}
            disabled={calculating}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {calculating ? 'Calculating...' : 'Calculate Price'}
          </button>

          {result && (
            <>
              <button type="button" onClick={handleApply} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                Apply Final Price to Variant
              </button>
              {allVariants && allVariants.length > 0 && onApplyToAllVariants && (
                <button type="button" onClick={handleApplyToAll} className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700">
                  Apply to All Variants
                </button>
              )}
            </>
          )}
        </div>

        {result && (
          <div className="p-3 py-2 border rounded bg-gray-50 text-sm space-y-2">
            <div className="flex gap-2">
              <div className="font-semibold text-green-700  rounded flex-1">Final Buyer Price (FBP): ₹{result.breakdown.FBP}</div>

              {wsResult && wsResult.breakdown && <div className="font-semibold text-blue-700  rounded flex-1">WS Final Price: ₹{wsResult.breakdown.FBP}</div>}
            </div>

            <div className="grid grid-cols-3 gap-1 text-xs">
              <div>China Price (RMB): {result.breakdown.AP_RMB}</div>
              <div>China Price (INR): ₹{result.breakdown.AP_INR}</div>
              <div>Markup: ₹{result.breakdown.MP}</div>

              <div>Cost Price (CP): ₹{result.breakdown.CP}</div>
              <div>Local Shipping (LS): ₹{result.breakdown.LS}</div>
              <div>Customs Duty: ₹{result.breakdown.CD}</div>

              <div>Int&apos;l Freight: ₹{result.breakdown.IF}</div>
              <div>Last Mile: ₹{result.breakdown.LMD}</div>
              <div>Landing Cost (LC): ₹{result.breakdown.LC}</div>

              <div>Platform Fee: ₹{result.breakdown.PF}</div>
              <div>GST Product: ₹{result.breakdown.GST_PR}</div>
              <div>GST Freight: ₹{result.breakdown.GST_FR}</div>

              <div>GST Platform: ₹{result.breakdown.GST_PF}</div>
              <div>Chargeable Weight: {result.breakdown.chargeableWeight} kg</div>
            </div>

            {/* Chargeable Weight explanation at the bottom */}
            <div className="mt-3 pt-2 border-t text-xs space-y-1">
              <div className="font-semibold">Chargeable Weight Details</div>

              <div>
                Volumetric Weight (VW) = (L × B × H in cm) / 5000 ={' '}
                {result.breakdown.volumetricWeightKg ? `${result.breakdown.volumetricWeightKg} kg` : 'N/A (dimensions not available)'}
              </div>

              <div>Dead Weight (DW) = Actual weight from API = {result.breakdown.deadWeightKg ? `${result.breakdown.deadWeightKg} kg` : 'N/A (weight not available)'}</div>

              <div>
                {result.breakdown.chargeableWeightSource === 'volumetric' && 'CW is taken from volumetric weight (VW) because VW is greater than or equal to dead weight (DW).'}
                {result.breakdown.chargeableWeightSource === 'dead' && 'CW is taken from Dead Weight (DW) because DW is greater than Volumetric Weight (VW).'}
                {result.breakdown.chargeableWeightSource === 'default' &&
                  'Chargeable weight is set to the default 0.5 kg (500 grams) because dimensions and/or weight are not available, so default is used for comparison.'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
